/* eslint-disable react-hooks/exhaustive-deps, @nrwl/nx/enforce-module-boundaries */

import { ApolloProvider } from '@apollo/client';
import { zooplaTheme } from '@blueprint/theme';
import { ThemeProvider } from '@blueprint/ui';
import { cache } from '@emotion/css';
import { CacheProvider } from '@emotion/react';
import { globalStyles } from '@zoopla-eng/zds-components/themes';
import { useApollo } from '@zoopla/apollo';
import { NudgesTracker } from '@zoopla/nudges-tracker';
import {
    OptimizelyProvider,
    useOptimizelyProviderProps,
    WithOptimizelyPageProps
} from '@zoopla/optimizely';
import { wrapper } from '@zoopla/redux-store';
import 'intersection-observer';
import { NewHomesRedirectBanner } from '@zoopla/new-homes';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import { useEffect } from 'react';
import { CustomPageComponent } from '../../../libs/source/src/components/app/types';
import { getWebChromeLayout } from '../../../libs/source/src/shared/components/Layout/WebChrome';
import '../../../libs/source/src/shared/globalThisPolyfill'; // Required for older browsers e.g. Safari < v13
import {
    handleAuthPagesRouting,
    handleForSaleLandingRouting,
    handleToRentLandingRouting
} from '../../../libs/source/src/shared/helpers';
import useAnalytics from '../../../libs/source/src/shared/hooks/app/useAnalytics';
import { useFetchUser } from '../../../libs/source/src/shared/hooks/useFetchUser';
import { useRouter } from 'next/router';

export const CURRENT_PATH = 'current_path';
export const PREVIOUS_PATH = 'previous_path';

export const useStoragePageHistory = () => {
    const router = useRouter();

    useEffect(() => {
        const storage = window?.localStorage;

        if (!storage) {
            return;
        }
        const currentPath = storage.getItem(CURRENT_PATH);

        if (window.location.pathname === currentPath) {
            return;
        }

        if (currentPath) {
            storage.setItem(PREVIOUS_PATH, currentPath);
        }

        storage.setItem(CURRENT_PATH, window.location.pathname);
    }, [router.asPath]);
};

const OptimizelyDevToggles = dynamic<Readonly<Record<string, never>>>(
    () => import('@zoopla/optimizely').then((mod) => mod.DevToggle),
    { ssr: false }
);

type ExtendedPageProps = {
    initialApolloState: Record<string, unknown>;
} & WithOptimizelyPageProps;

interface CustomAppProps extends AppProps<ExtendedPageProps> {
    Component: AppProps<ExtendedPageProps>['Component'] &
        CustomPageComponent<ExtendedPageProps>;
    pageProps: ExtendedPageProps;
}

const isDev = process.env.NODE_ENV === 'development';

function AppWrapper({ Component, pageProps }: CustomAppProps) {
    const getLayout = Component.getLayout || getWebChromeLayout;
    const optimizelyProviderProps = useOptimizelyProviderProps(pageProps);

    useAnalytics();
    useFetchUser();

    useEffect(() => {
        if (document) {
            document.documentElement.className = 'js';
        }
        NudgesTracker.setSession();
    }, []);
    const client = useApollo(pageProps.initialApolloState);

    useStoragePageHistory();

    useEffect(() => {
        if (!isDev) {
            Router.events.on('routeChangeStart', handleAuthPagesRouting);
            Router.events.on('routeChangeStart', handleForSaleLandingRouting);
            Router.events.on('routeChangeStart', handleToRentLandingRouting);

            return () => {
                Router.events.off(
                    'routeChangeComplete',
                    handleAuthPagesRouting
                );

                Router.events.off(
                    'routeChangeComplete',
                    handleForSaleLandingRouting
                );

                Router.events.off(
                    'routeChangeComplete',
                    handleToRentLandingRouting
                );
            };
        }
    }, [Router.events]);

    return (
        <OptimizelyProvider {...optimizelyProviderProps}>
            <CacheProvider value={cache}>
                <ThemeProvider theme={zooplaTheme}>
                    {globalStyles()}
                    {isDev && <OptimizelyDevToggles />}
                    <ApolloProvider client={client}>
                        {getLayout(<Component {...pageProps} />)}
                        <NewHomesRedirectBanner />
                    </ApolloProvider>
                </ThemeProvider>
            </CacheProvider>
        </OptimizelyProvider>
    );
}

const appWithRedux = wrapper.withRedux(AppWrapper);

export default appWithRedux;
